import * as _ from 'lodash';
import { allJurisdictionsCode, JurisdictionType } from './jurisdictions';
import { RuleCategory } from '../../services/RuleCategory';

export const PermissionEnum = Object.freeze({
    CREATE_RULE_ALL: 'CREATE_RULE_ALL',
    CREATE_RULE_AML: 'CREATE_RULE_AML',
    CREATE_RULE_FRAUD: 'CREATE_RULE_FRAUD',
    CREATE_RULE_RG: 'CREATE_RULE_RG',
    CREATE_TEAM: 'CREATE_TEAM',
    CREATE_USER: 'CREATE_USER',
    LOGIN: 'LOGIN',
    MANAGE_SIMULATIONS: 'MANAGE_SIMULATIONS',
    MANAGE_USERS: 'MANAGE_USERS',
    UPDATE_ALERT_DELEGATE: 'UPDATE_ALERT_DELEGATE',
    UPDATE_ALERT_DISMISS: 'UPDATE_ALERT_DISMISS',
    UPDATE_RULE_ALL: 'UPDATE_RULE_ALL',
    UPDATE_RULE_AML: 'UPDATE_RULE_AML',
    UPDATE_RULE_FRAUD: 'UPDATE_RULE_FRAUD',
    UPDATE_RULE_RG: 'UPDATE_RULE_RG',
    UPDATE_TEAM: 'UPDATE_TEAM',
    UPDATE_USER: 'UPDATE_USER',
    VIEW_AUDIT_LOG: 'VIEW_AUDIT_LOG',
    VIEW_TEAM_LIST: 'VIEW_TEAM_LIST',
    VIEW_USER_LIST: 'VIEW_USER_LIST'
});

export const extractPermissionsForUser = user => {
    const permissions = _.flatten(
        (user.belongsInTeams || []).map(team => team.permissions)
    );

    return _.sortBy(
        _.uniqBy(permissions, permission => permission),
        p => p
    );
};
export const permissionsToHideArray = [
    PermissionEnum.LOGIN,
    PermissionEnum.CREATE_RULE_ALL,
    PermissionEnum.CREATE_RULE_AML,
    PermissionEnum.CREATE_RULE_RG,
    PermissionEnum.CREATE_RULE_FRAUD,
    PermissionEnum.UPDATE_RULE_ALL,
    PermissionEnum.CREATE_USER,
    PermissionEnum.UPDATE_USER,
    PermissionEnum.VIEW_USER_LIST,
    PermissionEnum.CREATE_TEAM,
    PermissionEnum.UPDATE_TEAM,
    PermissionEnum.VIEW_TEAM_LIST
];

export const permissionsToDisplay = _.filter(
    PermissionEnum,
    permission => !_.includes(permissionsToHideArray, permission)
);

// The backend checks these without looking at jurisdiction or brand
// (AuthorisationService.auth(Permission)): a team that has one grants it
// everywhere, even when the team has no jurisdictions at all. VIEW_AUDIT_LOG
// reads the audit trail (not the events of a sandbox, which whoever may see the
// sandbox reads).
export const unscopedPermissions = [
    PermissionEnum.MANAGE_USERS,
    PermissionEnum.MANAGE_SIMULATIONS,
    PermissionEnum.VIEW_AUDIT_LOG
];

const toScope = code =>
    code === null || code === undefined ? allJurisdictionsCode : `${code}`;

/**
 * Whether the user's permissions grant `permission` in a jurisdiction and brand.
 *
 * userPermissions are the `PERMISSION:JURISDICTION:BRAND` strings returned by
 * GET /users/{id}, where ALL stands for every jurisdiction or every brand. A
 * missing jurisdiction or brand means "all of them", as it does when "All
 * jurisdictions" is selected. This mirrors AuthorisationService.verifyAuth on
 * the backend: the grant must cover the jurisdiction (or ALL) and the brand
 * (or ALL).
 *
 * A mapping to ALL jurisdictions never covers a sandbox (a jurisdiction of type
 * SIMULATION), so for one only the grants that name the sandbox count:
 * `PERMISSION:<sandbox>:ALL` and `PERMISSION:<sandbox>:<brand>`. Unscoped
 * permissions do not look at the jurisdiction, so a sandbox does not change them.
 *
 * @param {string[]} userPermissions
 * @param {string} permission a PermissionEnum value
 * @param {string} [jurisdictionCode]
 * @param {string|number} [brandId]
 * @param {string} [jurisdictionType] a JurisdictionType; a missing one is PRODUCTION
 * @return {boolean}
 */
export const hasPermission = (
    userPermissions,
    permission,
    jurisdictionCode,
    brandId,
    jurisdictionType
) => {
    const granted = userPermissions || [];

    if (_.includes(unscopedPermissions, permission)) {
        return granted.some(
            grantedPermission => grantedPermission.split(':')[0] === permission
        );
    }

    const jurisdictions =
        jurisdictionType === JurisdictionType.SIMULATION
            ? [toScope(jurisdictionCode)]
            : _.uniq([allJurisdictionsCode, toScope(jurisdictionCode)]);
    const brands = _.uniq([allJurisdictionsCode, toScope(brandId)]);

    return jurisdictions.some(jurisdiction =>
        brands.some(brand =>
            _.includes(granted, `${permission}:${jurisdiction}:${brand}`)
        )
    );
};

/**
 * Whether the user's permissions grant `permission` for a jurisdiction, for at least one of its brands.
 *
 * The permission must be mapped to that jurisdiction (to any of its brands) or to ALL jurisdictions; the brand that is selected at the
 * top does not matter. It is not what lets a user change a rule or the risk configuration (that needs every brand, see
 * hasPermissionForAllBrands), but a user who passes this and not that holds the permission for some of the brands only, which is
 * what the server refuses with BRANDS_NOT_COVERED.
 *
 * As in hasPermission, a missing jurisdiction means ALL, and a mapping to ALL jurisdictions never covers a sandbox (a jurisdiction of
 * type SIMULATION): only a grant that names the sandbox counts.
 *
 * @param {string[]} userPermissions the `PERMISSION:JURISDICTION:BRAND` strings returned by GET /users/{id}
 * @param {string} permission a PermissionEnum value
 * @param {string} [jurisdictionCode]
 * @param {string} [jurisdictionType] a JurisdictionType; a missing one is PRODUCTION
 * @return {boolean}
 */
export const hasPermissionInJurisdiction = (
    userPermissions,
    permission,
    jurisdictionCode,
    jurisdictionType
) => {
    if (_.includes(unscopedPermissions, permission)) {
        return hasPermission(userPermissions, permission);
    }

    const jurisdictions =
        jurisdictionType === JurisdictionType.SIMULATION
            ? [toScope(jurisdictionCode)]
            : _.uniq([allJurisdictionsCode, toScope(jurisdictionCode)]);

    return (userPermissions || []).some(granted => {
        const [name, jurisdiction] = granted.split(':');

        return name === permission && _.includes(jurisdictions, jurisdiction);
    });
};

/**
 * Whether the user's permissions grant `permission` for every brand of a jurisdiction.
 *
 * This is how the server decides who may change a rule (PUT /rules/{id}) or the risk configuration (PUT /risk/config/{jurisdiction}/...)
 * since legaleyse-backend#92: they belong to the jurisdiction and apply to every brand in it, so AuthorisationService.authOR asks for the
 * permission for all of them. Over all the user's teams, the grant covers the jurisdiction when
 * - it names the jurisdiction, or ALL jurisdictions, with ALL brands (which also covers brands made later); or
 * - the brands that the grants of the jurisdiction and of ALL jurisdictions name together include every brand the jurisdiction has now.
 * A brand made later and missing from such a list makes it stop covering. A jurisdiction with no brands is covered by ALL brands alone.
 *
 * As in hasPermission, a missing jurisdiction means ALL, and a mapping to ALL jurisdictions never covers a sandbox (a jurisdiction of type
 * SIMULATION). The list of brands of a sandbox that the client has is only the ones the user may see, so it cannot say that a list covers
 * all of them: only a grant of the sandbox with ALL brands counts for one.
 *
 * @param {string[]} userPermissions the `PERMISSION:JURISDICTION:BRAND` strings returned by GET /users/{id}
 * @param {string} permission a PermissionEnum value
 * @param {string} [jurisdictionCode]
 * @param {string} [jurisdictionType] a JurisdictionType; a missing one is PRODUCTION
 * @param {Array<string|number>} [brandIdsOfJurisdiction] the ids of the brands the jurisdiction has, from /general/brandsandjurisdictions
 * @return {boolean}
 */
export const hasPermissionForAllBrands = (
    userPermissions,
    permission,
    jurisdictionCode,
    jurisdictionType,
    brandIdsOfJurisdiction
) => {
    if (_.includes(unscopedPermissions, permission)) {
        return hasPermission(userPermissions, permission);
    }

    const isSandbox = jurisdictionType === JurisdictionType.SIMULATION;
    const jurisdictions = isSandbox
        ? [toScope(jurisdictionCode)]
        : _.uniq([allJurisdictionsCode, toScope(jurisdictionCode)]);
    const grantedBrands = (userPermissions || [])
        .map(granted => granted.split(':'))
        .filter(
            ([name, jurisdiction]) =>
                name === permission && _.includes(jurisdictions, jurisdiction)
        )
        .map(([, , brand]) => brand);

    if (_.includes(grantedBrands, allJurisdictionsCode)) {
        return true;
    }

    const brandIds = isSandbox ? [] : _.map(brandIdsOfJurisdiction, String);

    return (
        brandIds.length > 0 &&
        brandIds.every(brandId => _.includes(grantedBrands, brandId))
    );
};

const updatePermissionOfRuleCategory = {
    [RuleCategory.ANTI_MONEY_LAUNDERING]: PermissionEnum.UPDATE_RULE_AML,
    [RuleCategory.RESPONSIBLE_GAMBLING]: PermissionEnum.UPDATE_RULE_RG,
    [RuleCategory.FRAUD]: PermissionEnum.UPDATE_RULE_FRAUD
};

/**
 * The permissions that let a user change a rule of the category (edit it, enable it, disable it): UPDATE_RULE_ALL, or the one of its
 * category. The same set as AuthorisationService.getUpdatePermissionsForRule; any other category needs UPDATE_RULE_ALL.
 */
export const updateRulePermissions = ruleCategory =>
    _.compact([
        PermissionEnum.UPDATE_RULE_ALL,
        updatePermissionOfRuleCategory[ruleCategory]
    ]);

/** Risk weights drive every player's risk score in the jurisdiction, so the server asks for UPDATE_RULE_ALL, not a category's. */
export const updateRiskConfigPermissions = [PermissionEnum.UPDATE_RULE_ALL];

/**
 * Whether the user may change the rules of the category in the jurisdiction, as the server decides it: with one of the permissions for
 * every brand of the jurisdiction (see hasPermissionForAllBrands, which says what the last parameter is). The permissions are judged one
 * by one, as the server does: UPDATE_RULE_AML for some brands and UPDATE_RULE_ALL for the others do not add up.
 */
export const canUpdateRules = (
    userPermissions,
    ruleCategory,
    jurisdictionCode,
    jurisdictionType,
    brandIdsOfJurisdiction
) =>
    updateRulePermissions(ruleCategory).some(permission =>
        hasPermissionForAllBrands(
            userPermissions,
            permission,
            jurisdictionCode,
            jurisdictionType,
            brandIdsOfJurisdiction
        )
    );

/** Whether the user may change the risk configuration of the jurisdiction, as the server decides it (see canUpdateRules). */
export const canUpdateRiskConfig = (
    userPermissions,
    jurisdictionCode,
    jurisdictionType,
    brandIdsOfJurisdiction
) =>
    updateRiskConfigPermissions.some(permission =>
        hasPermissionForAllBrands(
            userPermissions,
            permission,
            jurisdictionCode,
            jurisdictionType,
            brandIdsOfJurisdiction
        )
    );

const holdsForSomeBrandsOnly = (
    permissions,
    userPermissions,
    jurisdictionCode,
    jurisdictionType,
    brandIdsOfJurisdiction
) =>
    !permissions.some(permission =>
        hasPermissionForAllBrands(
            userPermissions,
            permission,
            jurisdictionCode,
            jurisdictionType,
            brandIdsOfJurisdiction
        )
    ) &&
    permissions.some(permission =>
        hasPermissionInJurisdiction(
            userPermissions,
            permission,
            jurisdictionCode,
            jurisdictionType
        )
    );

/**
 * Whether the user holds a permission to change the rules of the category in the jurisdiction for some of its brands, and so may not
 * (canUpdateRules is false): what the server refuses with BRANDS_NOT_COVERED. A screen says so, since "you don't have permission" is
 * not what such a user needs to hear.
 */
export const holdsRulesForSomeBrandsOnly = (
    userPermissions,
    ruleCategory,
    jurisdictionCode,
    jurisdictionType,
    brandIdsOfJurisdiction
) =>
    holdsForSomeBrandsOnly(
        updateRulePermissions(ruleCategory),
        userPermissions,
        jurisdictionCode,
        jurisdictionType,
        brandIdsOfJurisdiction
    );

/** Whether the user holds the permission to change the risk configuration of the jurisdiction for some of its brands only (see holdsRulesForSomeBrandsOnly). */
export const holdsRiskConfigForSomeBrandsOnly = (
    userPermissions,
    jurisdictionCode,
    jurisdictionType,
    brandIdsOfJurisdiction
) =>
    holdsForSomeBrandsOnly(
        updateRiskConfigPermissions,
        userPermissions,
        jurisdictionCode,
        jurisdictionType,
        brandIdsOfJurisdiction
    );
